import {
  ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, OnDestroy, Output
} from '@angular/core';
import {
  AbstractControl, ControlValueAccessor, NG_VALUE_ACCESSOR, ValidationErrors, Validator
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';

import { Field, Option } from 'ngx-angular-query-builder';

import { TagInfoFragment, TagType } from 'generated/graphql';

import {
  DateRangeUsersDialogCloseData, DateRangeUsersOperatorDialog
} from 'app/query-common/dialogs/date-range-users-operator/date-range-users-operator.dialog';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { getTagTypeIcon } from 'field-shared/field-helper-functions';
import { addRemove, getSafe } from 'helpers';
import { DayTimeModel } from 'shared/components/day-time-input/day-time-input.component';
import {
  RangeOrRelativeDateInputModel
} from 'shared/components/range-or-relative-date-input/range-or-relative-date-input.component';

import {
  DateTagDateRangeModel
} from '../date-tag-range-query-input/date-tag-range-query-input.component';

import type { TagsAdvancedRuleValue } from '../../rule-interfaces/TagsAdvancedRuleValue';
import type { TagRule } from '../../rule-interfaces/TagRule';

@Component({
  selector: 'app-tags-advanced-rule-input',
  templateUrl: './tags-advanced-rule-input.component.html',
  styleUrls: ['./tags-advanced-rule-input.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TagsAdvancedRuleInputComponent), multi: true },
  ]
})
export class TagsAdvancedRuleInputComponent
  extends WithDisposableSubject() implements ControlValueAccessor, Validator, OnDestroy {

  /** Show inline operator */
  @Input() showInlineOperator = false;
  @Input() lockIncludeExclude = false;
  @Input() operator: string;
  @Output() operatorChange = new EventEmitter<string>();

  /** Field configuration (not used) */
  @Input() field: Field;
  @Input() targetType: 'EntityType' | 'EntityConnectionType' | 'CalendarEvent';
  @Input() campaignId: string;

  /** NOT USED */
  @Input() options: Option[];
  /** Disabled state of the input */
  @Input() disabled: boolean;

  showDateRange: boolean;
  showRange = {};

  limit = 100;
  /**
   * When there are a ton of tags, we don't want to show them all at once.
   */
  visibleTagRules: TagRule[] = [];

  tagRules: TagRule[] = [];

  createdByUserIds: string[] = [];

  dateApplied: RangeOrRelativeDateInputModel;

  @Input()
  get value(): TagsAdvancedRuleValue {
    return {
      tags: this.tagRules,
      date_applied: {
        relative_offset: this.dateApplied?.relativeOffset,
        start_date: this.dateApplied?.startDate,
        end_date: this.dateApplied?.endDate
      },
      createdByUsers: this.createdByUserIds.map(id => ({ id }))
    };
  }

  set value(value: TagsAdvancedRuleValue) {
    this.dateApplied = {
      relativeOffset: value?.date_applied?.relative_offset,
      /**ISO Formatted */
      startDate: value?.date_applied?.start_date,
      /**ISO Formatted */
      endDate: value?.date_applied?.end_date
    };
    this.tagRules = value?.tags || [];
    this.selectedResponses = value?.tags?.map(({ id }) => ({ id })) || [];
    this.createdByUserIds = this.extractCreatedByFromTagRules(this.tagRules);
    this.handleDataChange();
  }

  /** Number of responses shown by default */
  selectedResponses = [];

  // For ControlValueAccessor interface
  public onChangeCallback: () => void;
  public onTouchedCallback: () => any;

  constructor(private _cd: ChangeDetectorRef, private _matDialog: MatDialog) {
    super();
  }


  showMoreClicked() {
    this.limit += 100;
    this.setVisibleTagRules();
    this._cd.markForCheck();
  }

  setVisibleTagRules() {
    this.visibleTagRules = this.tagRules?.slice(0, this.limit) || [];
  }

  operatorChanged(operator: string) {
    if (!this.showInlineOperator) {
      return;
    }
    this.operator = operator;
    this.operatorChange.emit(operator);
  }


  clearSelectedResponses() {
    this.reset();
  }

  reset() {
    this.tagRules = [];
    this.selectedResponses = [];
    this.createdByUserIds = [];
    this.dateApplied = {
      relativeOffset: null,
      startDate: null,
      endDate: null
    };
    if (this.showInlineOperator) {
      this.operator = 'include having any';
      this.operatorChange.emit(this.operator);
    }
    this.handleDataChange();
  }

  usersChanged(userIds: string[]) {
    this.createdByUserIds = userIds;
    this.tagRules?.forEach(tr => {
      tr.created_by = userIds;
    }
    );
    this.handleDataChange();
  }

  dateAppliedChanged(event: RangeOrRelativeDateInputModel) {
    this.dateApplied = event;
    this._cd.markForCheck();
    this.tagRules?.forEach(tr => {
      tr.start_date = event.startDate;
      tr.end_date = event.endDate;
      tr.relative_offset = event.relativeOffset;
    });
    this.handleDataChange();
  }

  dateRangeAndUsersClicked(_event: DateRangeUsersDialogCloseData) {
    const dialogRef = DateRangeUsersOperatorDialog.open(this._matDialog, {
      data: {
        campaignId: this.campaignId,
        title: `${this.targetType === 'EntityType' ? 'Entity' : 'Connection'} Responses`,
        dateRange: this.dateApplied,
        userIds: this.createdByUserIds,
        showOperators: this.showInlineOperator,
        operator: this.operator,
        forTypeText: 'responses',
        targetType: this.targetType
      }
    });

    dialogRef.afterClosed().subscribe(
      (result) => {
        if (result) {
          this.dateAppliedChanged(result.dateRange);
          this.operatorChanged(result.operator);
          this.usersChanged(result.userIds);
        }
      });
  }

  getTagIcon(tagType: TagType) {
    const iconClass = getTagTypeIcon(tagType);
    return iconClass ? iconClass : 'fa-tag';
  }

  onSelectedResponsesChanged(responses) {
    const { add, remove } = addRemove(
      responses,
      this.selectedResponses || []
    );

    add.forEach(a => this.addTag(a));
    remove.forEach(r => this.removeTag(r));
  }

  removeTag(tag: TagInfoFragment) {
    this.tagRules = this.tagRules?.filter(r => r?.id !== tag.id) || [];
    this.selectedResponses = (this.selectedResponses || []).filter(t => t.id !== tag.id);
    this.handleDataChange();
  }

  addTag(tag: TagInfoFragment) {
    const newRule = { id: tag.id, tag_type: tag.tagType };
    this.tagRules = [...(this.tagRules || []), newRule];
    this.selectedResponses = [...(this.selectedResponses || []), tag];
    this.handleDataChange();
  }

  onRemoveRuleClick(rule) {
    this.tagRules = this.tagRules.filter(qti => qti !== rule);
    this.selectedResponses = (this.selectedResponses || []).filter(st => st.id !== rule.id);
    this.handleDataChange();
  }

  attachmentsOnlyCheckboxChanged(rule: TagRule) {
    rule.only_with_attachments = !rule?.only_with_attachments;
    this.handleDataChange();
  }

  // --- Date Tag Related --

  dateTagItemUpdated(rule: TagRule, event: DateTagDateRangeModel) {
    rule.range_start = event.range_start;
    rule.range_end = event.range_end;
    rule.date_relative_offset = event.date_relative_offset;
    this.handleDataChange();
  }


  // --- Shift Tag Related --

  shiftStartChanged(rule: TagRule, event: DayTimeModel) {
    rule.shift_start = event;
    this.handleDataChange();
  }

  shiftEndChanged(rule: TagRule, event: DayTimeModel) {
    rule.shift_end = event;
    this.handleDataChange();
  }

  // --- Number Tag Related --

  rangeStartChanged(rule: TagRule, event: string) {
    rule.range_start = event;
    this.handleDataChange();
  }

  rangeEndChanged(rule: TagRule, event: string) {
    rule.range_end = event;
    this.handleDataChange();
  }

  // ----------Validator Implementation----------
  validate(_control: AbstractControl): ValidationErrors | null {
    return null;
  }

  writeValue(obj: TagsAdvancedRuleValue): void {
    this.value = obj;
    const datePart = obj?.date_applied;
    this.showDateRange = !!(datePart?.end_date || datePart?.start_date || datePart?.relative_offset);
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = () => fn(this.value);
  }
  registerOnTouched(fn: any): void {
    this.onTouchedCallback = () => fn(this.value);
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // ----------END----------

  trackByTagId(a, b) {
    const id = getSafe(() => b.id, a);
    return id;
  }

  private extractCreatedByFromTagRules(tagRules: TagRule[]) {
    const createdByIds = tagRules?.map(({ created_by }) => created_by).flat().filter(id => !!id) || [];
    return [...new Set(createdByIds)];
  }

  private handleDataChange(): void {
    this.setVisibleTagRules();
    this._cd.markForCheck();
    if (this.onChangeCallback) {
      this.onChangeCallback();
    }
  }
}
